/**
 * Cloudflare R2 Direct Client Uploader with AWS SigV4 (Pure Native WebCrypto)
 * 
 * Works seamlessly in React without requiring heavy external AWS SDK packages.
 * Compresses images client-side before upload to preserve bandwidth & storage.
 */

// Cloudflare R2 Configuration from Environment Variables
const R2_ACCOUNT_ID = process.env.REACT_APP_R2_ACCOUNT_ID || "";
const R2_BUCKET_NAME = process.env.REACT_APP_R2_BUCKET_NAME || "aminii-media";
const R2_ACCESS_KEY_ID = process.env.REACT_APP_R2_ACCESS_KEY_ID || "";
const R2_SECRET_ACCESS_KEY = process.env.REACT_APP_R2_SECRET_ACCESS_KEY || "";
const R2_PUBLIC_URL = (process.env.REACT_APP_R2_PUBLIC_URL || "").replace(/\/$/, "");

/**
 * Validates whether R2 is configured
 */
export function isR2Configured() {
  return Boolean(R2_ACCOUNT_ID && R2_BUCKET_NAME && R2_ACCESS_KEY_ID && R2_SECRET_ACCESS_KEY);
}

/**
 * Detects whether this browser's canvas can actually encode WebP.
 * Some mobile browsers accept "image/webp" without error but silently
 * return a lossless PNG instead — this check catches that case so we
 * can fall back to a format that's guaranteed to compress.
 */
let webpSupportedPromise = null;
function isWebpEncodeSupported() {
  if (webpSupportedPromise) return webpSupportedPromise;
  webpSupportedPromise = new Promise((resolve) => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 1;
      canvas.height = 1;
      canvas.toBlob((blob) => resolve(!!blob && blob.type === "image/webp"), "image/webp");
    } catch (e) {
      resolve(false);
    }
  });
  return webpSupportedPromise;
}

/**
 * Compresses an image in the browser using HTML5 Canvas
 * - Max dimension: 1200px (retina-ready)
 * - Format: WebP where the browser can actually encode it, JPEG otherwise
 *   (never lets the browser silently fall back to a lossless format)
 * - Quality: 0.82 default (crisp & visual loss-less, ~100-150KB)
 *
 * @returns {Promise<Blob>} the compressed blob — check `blob.type` for the
 *   real encoded format instead of assuming WebP.
 */
export async function compressImage(file, maxDimension = 1200, quality = 0.82) {
  const webpSupported = await isWebpEncodeSupported();
  const targetMimeType = webpSupported ? "image/webp" : "image/jpeg";

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxDimension) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!webpSupported) {
          // JPEG has no alpha channel — flatten onto white so transparent
          // PNGs/etc don't turn black.
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Canvas toBlob failed"));
            }
          },
          targetMimeType,
          quality
        );
      };
      img.onerror = () => reject(new Error("Image load failed for compression"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });
}

/**
 * Maps a compressed blob's real MIME type to a file extension + content type,
 * so callers never have to assume/hardcode "webp".
 */
export function getCompressedFileInfo(blob) {
  const type = blob.type || "image/jpeg";
  const extension = type === "image/webp" ? "webp" : type === "image/png" ? "png" : "jpg";
  return { contentType: type, extension };
}

/**
 * Helper: Computes SHA-256 hash using native Web Crypto
 */
async function sha256(strOrBuffer) {
  const data = typeof strOrBuffer === "string" ? new TextEncoder().encode(strOrBuffer) : strOrBuffer;
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Helper: Computes HMAC-SHA256 using native Web Crypto
 */
async function hmac(key, str) {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const signature = await crypto.subtle.sign("HMAC", cryptoKey, new TextEncoder().encode(str));
  return new Uint8Array(signature);
}

/**
 * Helper: Derives AWS SigV4 signing key
 */
async function getSignatureKey(key, dateStamp, regionName, serviceName) {
  const kDate = await hmac(new TextEncoder().encode("AWS4" + key), dateStamp);
  const kRegion = await hmac(kDate, regionName);
  const kService = await hmac(kRegion, serviceName);
  const kSigning = await hmac(kService, "aws4_request");
  return kSigning;
}

/**
 * Builds the public URL for an object key without uploading anything.
 */
export function getPublicUrlForKey(destinationKey) {
  if (R2_PUBLIC_URL) {
    return `${R2_PUBLIC_URL}/${destinationKey}`;
  }
  // Path-style, matching the actual endpoint used to PUT/list objects below
  // (not the bucket-subdomain "virtual-hosted" style) — keeps this in sync
  // with getR2KeyFromUrl's parsing.
  return `https://${R2_ACCOUNT_ID}.r2.cloudflarestorage.com/${R2_BUCKET_NAME}/${destinationKey}`;
}

/**
 * Inverse of getPublicUrlForKey: given a product image URL, returns the R2
 * object key if that URL actually points at this app's R2 bucket, or null
 * otherwise (e.g. a leftover Cloudinary URL).
 */
export function getR2KeyFromUrl(url) {
  if (!url) return null;
  if (R2_PUBLIC_URL && url.startsWith(R2_PUBLIC_URL + "/")) {
    return url.slice(R2_PUBLIC_URL.length + 1);
  }
  const cfMatch = url.match(/\.r2\.cloudflarestorage\.com\/[^/]+\/(.+)$/);
  if (cfMatch) return decodeURIComponent(cfMatch[1]);
  return null;
}

/**
 * Lists every object key already present under a prefix in one (or a few,
 * if paginated) request — meant to be called ONCE before a batch of
 * uploads, so callers can skip re-uploading files that already exist
 * instead of paying for a PUT (or a HEAD) per file.
 *
 * @returns {Promise<Set<string>>} set of existing object keys
 */
export async function listExistingKeys(prefix = "") {
  if (!isR2Configured()) return new Set();

  const endpointHost = `${R2_ACCOUNT_ID}.r2.cloudflarestorage.com`;
  const region = "auto";
  const service = "s3";
  const canonicalUri = `/${R2_BUCKET_NAME}`;
  const keys = new Set();
  let continuationToken;

  do {
    const now = new Date();
    const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, "");
    const dateStamp = amzDate.substr(0, 8);

    const queryParams = { "list-type": "2", "max-keys": "1000", prefix };
    if (continuationToken) queryParams["continuation-token"] = continuationToken;
    const canonicalQuery = Object.keys(queryParams)
      .sort()
      .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(queryParams[k])}`)
      .join("&");

    const payloadHash = await sha256("");
    const canonicalHeaders = `host:${endpointHost}\nx-amz-content-sha256:${payloadHash}\nx-amz-date:${amzDate}\n`;
    const signedHeaders = "host;x-amz-content-sha256;x-amz-date";
    const canonicalRequest = `GET\n${canonicalUri}\n${canonicalQuery}\n${canonicalHeaders}\n${signedHeaders}\n${payloadHash}`;

    const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
    const hashedCanonicalRequest = await sha256(canonicalRequest);
    const stringToSign = `AWS4-HMAC-SHA256\n${amzDate}\n${credentialScope}\n${hashedCanonicalRequest}`;

    const signingKey = await getSignatureKey(R2_SECRET_ACCESS_KEY, dateStamp, region, service);
    const signatureBytes = await hmac(signingKey, stringToSign);
    const signature = Array.from(signatureBytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    const authorizationHeader = `AWS4-HMAC-SHA256 Credential=${R2_ACCESS_KEY_ID}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

    const response = await fetch(`https://${endpointHost}${canonicalUri}?${canonicalQuery}`, {
      method: "GET",
      headers: {
        Host: endpointHost,
        "x-amz-content-sha256": payloadHash,
        "x-amz-date": amzDate,
        Authorization: authorizationHeader,
      },
    });

    if (!response.ok) {
      throw new Error(`R2 list failed with HTTP ${response.status}: ${await response.text()}`);
    }

    const xmlText = await response.text();
    const doc = new DOMParser().parseFromString(xmlText, "application/xml");
    const contentsNodes = doc.getElementsByTagName("Contents");
    for (let i = 0; i < contentsNodes.length; i++) {
      const keyNode = contentsNodes[i].getElementsByTagName("Key")[0];
      if (keyNode && keyNode.textContent) keys.add(keyNode.textContent);
    }
    const isTruncated = doc.getElementsByTagName("IsTruncated")[0]?.textContent === "true";
    continuationToken = isTruncated
      ? doc.getElementsByTagName("NextContinuationToken")[0]?.textContent
      : undefined;
  } while (continuationToken);

  return keys;
}

/**
 * Uploads a file directly to Cloudflare R2 using AWS SigV4
 *
 * @param {Blob|File} fileBlob - Compressed image or file
 * @param {string} destinationKey - Target path inside R2 (e.g. "products/IMG_4256.webp")
 * @param {string} contentType - MIME type (e.g. "image/webp")
 * @returns {Promise<string>} Public URL of the uploaded file
 */
export async function uploadToR2(fileBlob, destinationKey, contentType = "image/webp") {
  if (!isR2Configured()) {
    throw new Error(
      "Cloudflare R2 is not configured in .env. Missing REACT_APP_R2_ACCOUNT_ID, REACT_APP_R2_ACCESS_KEY_ID, or REACT_APP_R2_SECRET_ACCESS_KEY."
    );
  }

  const endpointHost = `${R2_ACCOUNT_ID}.r2.cloudflarestorage.com`;
  const url = `https://${endpointHost}/${R2_BUCKET_NAME}/${destinationKey}`;

  const now = new Date();
  const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, ""); // e.g. 20260916T155000Z
  const dateStamp = amzDate.substr(0, 8); // e.g. 20260916

  const arrayBuffer = await fileBlob.arrayBuffer();
  const payloadHash = await sha256(arrayBuffer);

  const region = "auto";
  const service = "s3";

  // 1. Canonical Request
  const canonicalUri = `/${R2_BUCKET_NAME}/${encodeURI(destinationKey)}`;
  const cacheControlValue = "public, max-age=31536000, immutable";
  const canonicalHeaders =
    `cache-control:${cacheControlValue}\n` +
    `host:${endpointHost}\n` +
    `x-amz-content-sha256:${payloadHash}\n` +
    `x-amz-date:${amzDate}\n`;
  const signedHeaders = "cache-control;host;x-amz-content-sha256;x-amz-date";
  const canonicalRequest = `PUT\n${canonicalUri}\n\n${canonicalHeaders}\n${signedHeaders}\n${payloadHash}`;

  // 2. String to Sign
  const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
  const hashedCanonicalRequest = await sha256(canonicalRequest);
  const stringToSign = `AWS4-HMAC-SHA256\n${amzDate}\n${credentialScope}\n${hashedCanonicalRequest}`;

  // 3. Signature
  const signingKey = await getSignatureKey(R2_SECRET_ACCESS_KEY, dateStamp, region, service);
  const signatureBytes = await hmac(signingKey, stringToSign);
  const signature = Array.from(signatureBytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  // 4. Authorization Header
  const authorizationHeader = `AWS4-HMAC-SHA256 Credential=${R2_ACCESS_KEY_ID}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

  // 5. Send HTTP PUT
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      Host: endpointHost,
      "Content-Type": contentType,
      "Cache-Control": cacheControlValue,
      "x-amz-content-sha256": payloadHash,
      "x-amz-date": amzDate,
      Authorization: authorizationHeader,
    },
    body: arrayBuffer,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`R2 Upload failed with HTTP ${response.status}: ${errorText}`);
  }

  return getPublicUrlForKey(destinationKey);
}
